import { motion } from "framer-motion";
import { Mic, Zap, Target } from "lucide-react";

function getAccuracyInfo(sampleCount: number) {
  if (sampleCount >= 5) {
    return { label: "Excellent", percent: 100, color: "hsl(142 70% 45%)", icon: Target };
  }
  if (sampleCount >= 3) {
    return { label: "Good", percent: 70, color: "hsl(188 90% 42%)", icon: Zap };
  }
  if (sampleCount >= 1) {
    return { label: "Basic", percent: Math.max(20, sampleCount * 20), color: "hsl(45 90% 55%)", icon: Mic };
  }
  return { label: "Not trained", percent: 0, color: "hsl(0 0% 40%)", icon: Mic };
}

export function VoiceAccuracyMeter({
  sampleCount,
  compact = false,
}: {
  sampleCount: number;
  compact?: boolean;
}) {
  const { label, percent, color, icon: Icon } = getAccuracyInfo(sampleCount);

  if (compact) {
    return (
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
            Voice Accuracy
          </span>
          <span className="text-xs font-bold" style={{ color }}>
            {label}
          </span>
        </div>
        <div className="h-1.5 bg-secondary/60 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ background: color }}
            initial={{ width: 0 }}
            animate={{ width: `${percent}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />
        </div>
        {sampleCount < 5 && (
          <p className="text-[10px] text-muted-foreground">
            {sampleCount < 3
              ? `Add ${3 - sampleCount} more sample${3 - sampleCount > 1 ? "s" : ""} to improve your AI voice match.`
              : `Add ${5 - sampleCount} more for excellent accuracy.`}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-card/50 border border-border rounded-2xl p-5 space-y-4">
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: `${color}20` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground">Voice Accuracy</h3>
          <p className="text-xs text-muted-foreground">
            {sampleCount} sample{sampleCount !== 1 ? "s" : ""} analyzed
          </p>
        </div>
        <div className="ml-auto">
          <span
            className="px-2.5 py-1 rounded-full text-xs font-bold"
            style={{ background: `${color}20`, color }}
          >
            {label}
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <div className="h-2.5 bg-secondary/60 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ background: color }}
            initial={{ width: 0 }}
            animate={{ width: `${percent}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
          />
        </div>
        <div className="flex justify-between text-[10px] text-muted-foreground">
          <span>Basic</span>
          <span>Good</span>
          <span>Excellent</span>
        </div>
      </div>

      {sampleCount < 5 && (
        <p className="text-xs text-muted-foreground">
          {sampleCount === 0
            ? "Add writing samples or record voice memos to train your AI voice."
            : sampleCount < 3
              ? `Add ${3 - sampleCount} more sample${3 - sampleCount > 1 ? "s" : ""} to improve your AI voice match.`
              : `Add ${5 - sampleCount} more sample${5 - sampleCount > 1 ? "s" : ""} for excellent accuracy.`}
        </p>
      )}
    </div>
  );
}
